import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, computed, effect, inject, input, signal, viewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, EMPTY, catchError, finalize, map, switchMap, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { Loading } from '@components/ui/loading/loading';
import { MsfSelect } from '@components/select/select';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { OrchestrationProcessSelectComponent } from '@app/pages/admin/components/orchestration-process-select/orchestration-process-select';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { MakeSubscriptionDialogComponent } from '@app/pages/admin/components/make-subscription-dialog/make-subscription-dialog';
import { defaultSubscriptionNameFromAssignment } from '@app/pages/admin/components/make-subscription-dialog/make-subscription-dialog.util';
import { AssignmentProcessService as AssignmentProcessApiService } from '@6grain/masofa-monolith-api-client/common/api/assignmentProcess.service';
import { OperationMonitoringService as OperationMonitoringApiService } from '@6grain/masofa-monolith-api-client/common/api/operationMonitoring.service';
import { operationDetailsUrl, operationDetailsUrlFromHash } from '@app/pages/admin/operation-details.util';
import { assignmentRowTitle, orchestrationSatelliteProductUrl } from '@app/pages/admin/orchestration-identity.util';
import { assignmentHasSatelliteProducts, assignmentStartApiError, isOrchestrationProbeProcess, processesVisibleOnAssignmentsList } from '@app/pages/admin/assignment-start.util';
import {
  AssignmentResultFilter,
  assignmentResultFilterAfterCreate,
  assignmentResultFilterQuery,
  formatAssignmentResultTooltip,
  readAssignmentResultCount,
} from '@app/pages/admin/assignment-result-count.util';
import { assignmentOutcome, assignmentTiming } from '@app/pages/admin/assignment-execution.util';
import { assignmentStatusAt } from '@app/pages/admin/assignment-status-at.util';
import { assignmentHostLabel, readAssignmentHost, readAssignmentWorker } from '@app/pages/admin/assignment-host.util';
import { AssignmentHostLabelComponent } from '@app/pages/admin/components/assignment-host-label/assignment-host-label';
import { ExecutorKeyDto, ExecutorKeysService } from '@app/services/executor-keys.service';
import { RestartFailedAssignmentsService } from '@app/services/restart-failed-assignments.service';
import { AssignmentResultFilterComponent } from '@app/pages/admin/components/assignment-result-filter/assignment-result-filter';
import { AssignmentResultCellComponent } from '@app/pages/admin/components/assignment-result-cell/assignment-result-cell';
import { AssignmentRssCellComponent } from '@app/pages/admin/components/assignment-rss-cell/assignment-rss-cell';
import { AssignmentRssView, readAssignmentRss } from '@app/pages/admin/assignment-rss';
import { AssignmentStartFormComponent } from '@app/pages/admin/orchestration-process-page/components/assignment-start-form/assignment-start-form';
import { MonitoredOperationListItemResponse } from '@6grain/masofa-monolith-api-client/common/model/monitoredOperationListItemResponse';
import { SatelliteAssignmentProductResponse } from '@6grain/masofa-monolith-api-client/common/model/satelliteAssignmentProductResponse';
import { StartAssignmentProcessRequest } from '@6grain/masofa-monolith-api-client/common/model/startAssignmentProcessRequest';
import { CustomPaginatorComponent } from '../../../Ui-Kit/custom-paginator/custom-paginator.component';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { DatePickerComponent } from '@components/date-picker/date-picker.component';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import {
  AssignmentTableFilters,
  assignmentTableFiltersEqualExceptSort,
  assignmentTableFiltersNeedReload,
  AssignmentListMode,
  openProcessGroupFilters,
  parseAssignmentTableFilterQuery,
  serializeAssignmentTableFilterQuery,
} from '@app/pages/admin/assignment-table-filter-query.util';
import {
  AssignmentSortColumn,
  AssignmentSortDirection,
  defaultAssignmentSort,
  sortAssignmentGroupRows,
  toggleAssignmentColumnSort,
} from '@app/pages/admin/assignment-table-sort.util';
import { ASSIGNMENT_ACTIVITY_WINDOW, ASSIGNMENT_FEED_SORT } from '@app/pages/admin/assignment-feed-sort.interceptor';
import { AssignmentProcessGroupsService, AssignmentProcessGroup } from '@app/services/assignment-process-groups.service';
import { AssignmentGroupRowView, toAssignmentGroupRow } from '@app/pages/admin/assignment-process-group.view';
import {
  ASSIGNMENT_STATE_FILTERS,
  AssignmentStateFilter,
  assignmentOperationsStateQuery,
  assignmentStateSeverity,
  canCancelAssignment,
  canRestartAssignment,
  createdRangeApiBounds,
  TagSeverity,
} from '@app/pages/admin/orchestration-process-page/orchestration-process-page.util';
import { canonicalProcessKey } from '@app/pages/admin/orchestration-process-keys.util';
import {
  WorkerChartWindow,
  workerChartActivityBounds,
} from '@app/pages/admin/worker-page/worker-chart.util';

interface ProcessDefinition {
  key: string;
  displayName?: string;
  description?: string;
  version: number;
}

type AssignmentRow = MonitoredOperationListItemResponse & {
  resultCount: number | null;
  resultTooltip: string | null;
  durationLabel: string;
  queueLabel: string;
  outcomeLabel: string;
  outcomeReason: string | null;
  hostLabel: string;
  workerId: string | null;
  workerServiceName: string | null;
  workerHostName: string | null;
  displayTitle: string;
  stateSeverity: TagSeverity;
  statusAt: string | null;
  canCancel: boolean;
  canRestart: boolean;
  hasSatelliteProducts: boolean;
  rss: AssignmentRssView | null;
};

type LoadedAssignmentList =
  | { kind: 'groups'; groups: AssignmentProcessGroup[] }
  | {
      kind: 'runs';
      items: MonitoredOperationListItemResponse[];
      totalCount: number;
      page: number;
      pageSize: number;
    };

@Component({
  selector: 'app-assignment-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    TagModule,
    TooltipModule,
    Loading,
    PrimaryButton,
    SecondaryButton,
    OrchestrationProcessSelectComponent,
    MakeSubscriptionDialogComponent,
    CopyableIdentifierComponent,
    AssignmentHostLabelComponent,
    DateFormatLangPipe,
    AssignmentStartFormComponent,
    AssignmentResultFilterComponent,
    AssignmentResultCellComponent,
    AssignmentRssCellComponent,
    OrchestrationTableHeadingComponent,
    DatePickerComponent,
    MsfSelect,
    CustomPaginatorComponent,
  ],
  templateUrl: './assignment-list.html',
  styleUrls: [
    './assignment-list.scss',
    '../../assignment-result-count.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
})
export class AssignmentListComponent implements OnInit, OnDestroy {
  private readonly assignments = inject(AssignmentProcessApiService);
  private readonly restartFailedApi = inject(RestartFailedAssignmentsService);
  private readonly groupsApi = inject(AssignmentProcessGroupsService);
  private readonly monitoring = inject(OperationMonitoringApiService);
  private readonly executorKeys = inject(ExecutorKeysService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();
  readonly translate = inject(TranslateService);
  /**
   * Enrollment key id, `monolith`, or a heartbeat worker id.
   * Id ключа, `monolith` или worker id heartbeat.
   * Hides the worker select and ignores query `worker`.
   * Прячет селект исполнителя и не читает query `worker`.
   */
  readonly lockedWorker = input<string | null>(null);
  /** Create stays on the assignments page. The worker card does not start runs. */
  readonly allowCreate = input(true);
  /** Batch restart of Failed stays on the assignments page. The worker card does not show it. */
  readonly allowRestartFailed = input(true);
  /** Worker-card chart window. Hides the Created picker and filters runs by createdAt. */
  readonly lockedWindow = input<WorkerChartWindow | null>(null);

  private readonly startForm = viewChild(AssignmentStartFormComponent);
  private readonly subscriptionDialog = viewChild(MakeSubscriptionDialogComponent);
  private readonly started = signal(false);
  private appliedLockedWorker: string | null = null;
  private appliedLockedWindow: WorkerChartWindow | null = null;

  processes: ProcessDefinition[] = [];
  processOptions: { id: string; label: string; key: string }[] = [];
  operations: AssignmentRow[] = [];
  groups: AssignmentGroupRowView[] = [];
  private rawGroups: AssignmentProcessGroup[] = [];
  listMode: AssignmentListMode = 'groups';
  listChips: { id: AssignmentListMode; label: string; selected: boolean }[] = [];
  readonly groupSortColumns: { id: AssignmentSortColumn; label: string }[] = [
    { id: 'name', label: 'Assignment name' },
    { id: 'last', label: 'Last run' },
  ];
  readonly runSortColumns: { id: AssignmentSortColumn; label: string }[] = [
    { id: 'name', label: 'Assignment name' },
    { id: 'state', label: 'Status' },
    { id: 'executedOn', label: 'Executed on' },
    { id: 'created', label: 'Created' },
    { id: 'progress', label: 'Progress' },
    { id: 'duration', label: 'Duration' },
    { id: 'result', label: 'Result' },
  ];
  sortColumn: AssignmentSortColumn = 'last';
  sortDir: AssignmentSortDirection = 'desc';
  sortAria: 'ascending' | 'descending' = 'descending';
  searchQuery = '';
  emptyListMessage = 'No assignments found';
  products: Array<SatelliteAssignmentProductResponse & { analysisUrl: string | null }> = [];
  loading = false;
  saving = false;
  dialogVisible = false;
  productsVisible = false;
  cancellationVisible = false;
  cancellationSaving = false;
  cancellationAssignmentId = '';
  restartVisible = false;
  restartSaving = false;
  restartAssignmentId = '';
  restartFailedVisible = false;
  restartFailedSaving = false;
  restartFailedEveryProcess = false;
  copyVisible = false;
  copySaving = false;
  copyAssignmentId = '';
  copyProcessKey = '';
  errorMessage = '';
  private openCreateFromQuery = this.route.snapshot.queryParamMap.get('create') === '1';
  processKey = 'satellite.find-products';
  processFilter: string | null = null;
  workerFilter: string | null = null;
  workerOptions: { id: string; label: string }[] = [];
  private enrollmentKeys: ExecutorKeyDto[] = [];
  resultFilter: AssignmentResultFilter = 'positive';
  stateFilter: AssignmentStateFilter = 'all';
  createdRange: Date[] | null = null;
  stateChips: { id: AssignmentStateFilter; label: string; selected: boolean }[] = [];
  page = 1;
  pageSize = 25;
  totalRecords = 0;
  first = 0;
  readonly pageSizeOptions = [10, 25, 50, 100];
  readonly canStartForm = computed(() => this.startForm()?.ready() === true);
  readonly workerSelectHidden = computed(() => !!this.lockedWorker()?.trim());
  private readonly operationsReload$ = new Subject<boolean>();
  private lastAppliedFilters: AssignmentTableFilters | null = null;

  constructor() {
    effect(() => {
      const worker = this.lockedWorker()?.trim() || null;
      if (!this.started()) {
        return;
      }
      if (worker === this.appliedLockedWorker) {
        return;
      }
      this.appliedLockedWorker = worker;
      this.resetToFirstPage();
      this.reloadOperations();
    });
    effect(() => {
      const chartWindow = this.lockedWindow();
      if (!this.started()) {
        return;
      }
      if (chartWindow === this.appliedLockedWindow) {
        return;
      }
      this.appliedLockedWindow = chartWindow;
      if (chartWindow) {
        this.createdRange = null;
      }
      this.resetToFirstPage();
      this.reloadOperations();
    });
  }

  ngOnInit(): void {
    this.appliedLockedWorker = this.lockedWorker()?.trim() || null;
    this.appliedLockedWindow = this.lockedWindow();
    this.started.set(true);
    this.searchQuery = this.route.snapshot.queryParamMap.get('search') ?? '';
    const detailsUrl = operationDetailsUrlFromHash(this.route.snapshot.fragment);
    if (!this.workerSelectHidden() && detailsUrl) {
      void this.router.navigateByUrl(detailsUrl);
      return;
    }
    this.loadProcesses();
    if (!this.workerSelectHidden()) {
      this.loadEnrollmentKeys();
    }
    this.rebuildStateChips();
    this.rebuildWorkerOptions();
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.rebuildWorkerOptions();
      this.publishGroups();
      this.cdr.markForCheck();
    });
    this.translate.get('Monolith').pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.rebuildWorkerOptions();
      this.cdr.markForCheck();
    });
    this.operationsReload$.pipe(
      switchMap(silent => {
        if (!silent) {
          this.loading = true;
          this.errorMessage = '';
          this.cdr.markForCheck();
        }
        const search = this.searchQuery.trim() || undefined;
        const state = assignmentOperationsStateQuery(this.stateFilter);
        const dates = this.createdBounds();
        const activity = this.activityBounds();
        const positiveResult = assignmentResultFilterQuery(this.resultFilter);
        const worker = this.effectiveWorker();
        const request = this.listMode === 'groups'
          ? this.groupsApi.load({
              state,
              search,
              processDefinitionKey: this.processFilter?.trim() || undefined,
              createdFrom: dates.createdFrom,
              createdTo: dates.createdTo,
              positiveResult,
              worker,
              activityFrom: activity.activityFrom,
              activityTo: activity.activityTo,
            }).pipe(
              map((groups): LoadedAssignmentList => ({ kind: 'groups', groups: groups ?? [] })),
            )
          : this.monitoring.monitoringOperationsGet(
              'Assignment',
              state,
              undefined,
              undefined,
              search,
              this.processFilter?.trim() || undefined,
              undefined,
              undefined,
              dates.createdFrom,
              dates.createdTo,
              undefined,
              undefined,
              this.page,
              this.pageSize,
              positiveResult,
              this.effectiveWorker(),
              undefined,
              undefined,
              'body',
              false,
              this.feedRequestOptions(),
            ).pipe(
              map((response): LoadedAssignmentList => ({
                kind: 'runs',
                items: response.items ?? [],
                totalCount: response.totalCount ?? 0,
                page: response.page ?? this.page,
                pageSize: response.pageSize ?? this.pageSize,
              })),
            );
        return request.pipe(
          catchError(error => this.keepListOnGetError(error)),
          finalize(() => {
            this.loading = false;
            this.cdr.markForCheck();
          }),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe(loaded => {
      if (loaded.kind === 'groups') {
        this.rawGroups = loaded.groups;
        this.publishGroups();
      } else {
        this.operations = loaded.items.map(item => this.toAssignmentRow(item));
        this.totalRecords = loaded.totalCount;
        this.page = loaded.page;
        this.pageSize = loaded.pageSize;
        this.first = (this.page - 1) * this.pageSize;
      }
      this.emptyListMessage = this.searchQuery.trim() ? 'No catalog matches' : 'No assignments found';
      if (this.openCreateFromQuery && this.allowCreate()) {
        this.dialogVisible = true;
        this.openCreateFromQuery = false;
      }
      this.cdr.markForCheck();
    });
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const parsed = parseAssignmentTableFilterQuery(params);
      if (this.workerSelectHidden()) {
        parsed.worker = '';
      }
      const dropCreatedRange = this.lockedWindow() != null
        && (params.get('createdFrom') != null || params.get('createdTo') != null);
      if (this.lockedWindow() != null) {
        parsed.createdRange = null;
      }
      const previous = this.lastAppliedFilters;
      const groupSortOnly = previous != null
        && (parsed.list ?? 'groups') !== 'runs'
        && (previous.list ?? 'groups') !== 'runs'
        && assignmentTableFiltersEqualExceptSort(previous, parsed)
        && (previous.sort !== parsed.sort || previous.dir !== parsed.dir);
      const shouldReload = assignmentTableFiltersNeedReload(previous, parsed);
      this.resultFilter = parsed.result;
      this.stateFilter = parsed.state;
      this.createdRange = parsed.createdRange;
      this.processFilter = parsed.process || null;
      this.workerFilter = this.workerSelectHidden() ? null : (parsed.worker || null);
      this.listMode = parsed.list ?? 'groups';
      this.sortColumn = parsed.sort ?? defaultAssignmentSort(this.listMode).column;
      this.sortDir = parsed.dir ?? 'desc';
      this.sortAria = this.sortDir === 'asc' ? 'ascending' : 'descending';
      this.rebuildStateChips();
      this.rebuildListChips();
      this.rebuildWorkerOptions();
      this.lastAppliedFilters = parsed;
      if (
        dropCreatedRange
        || params.get('group') != null
        || params.get('g') != null
        || this.assignmentSortQueryNeedsRewrite(params)
      ) {
        this.syncAssignmentFilterQuery();
      }
      if (groupSortOnly) {
        this.publishGroups();
        this.cdr.markForCheck();
        return;
      }
      if (!shouldReload) {
        this.cdr.markForCheck();
        return;
      }
      this.resetToFirstPage();
      this.reloadOperations();
    });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.operationsReload$.complete();
  }

  load(): void {
    this.reloadOperations();
  }

  /** Reloads the current filters. A silent reload keeps the rows and skips the busy overlay. */
  reload(silent = false): void {
    this.operationsReload$.next(silent);
  }

  private loadProcesses(): void {
    this.assignments.assignmentsProcessesGet().pipe(takeUntil(this.destroy$)).subscribe({
      next: processes => {
        this.processes = processesVisibleOnAssignmentsList(processes as ProcessDefinition[]);
        this.processOptions = this.processes.map(process => ({
          id: process.key,
          label: process.displayName || process.key,
          key: process.key,
        }));
        const requestedKey = this.route.snapshot.queryParamMap.get('processKey');
        if (requestedKey && this.processes.some(process => process.key === requestedKey)) {
          this.processKey = requestedKey;
        } else if (!this.processes.some(process => process.key === this.processKey)) {
          this.processKey = this.processes[0]?.key ?? '';
        }
        this.operations = this.operations.map(item => this.toAssignmentRow(item));
        this.publishGroups();
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorMessage = 'Failed to load assignments';
        this.cdr.markForCheck();
      },
    });
  }

  private loadEnrollmentKeys(): void {
    this.executorKeys.list().pipe(takeUntil(this.destroy$)).subscribe({
      next: keys => {
        this.enrollmentKeys = keys ?? [];
        this.rebuildWorkerOptions();
        this.cdr.markForCheck();
      },
      error: () => {
        this.enrollmentKeys = [];
        this.rebuildWorkerOptions();
        this.cdr.markForCheck();
      },
    });
  }

  private rebuildWorkerOptions(): void {
    const selected = this.workerFilter?.trim() ?? '';
    const selectedKey = selected.toLowerCase();
    const options = [
      { id: 'monolith', label: this.translate.instant('Monolith') },
      ...this.enrollmentKeys
        .filter(key => !!key.id)
        .map(key => ({
          id: key.id.toLowerCase(),
          label: key.name?.trim() || key.id,
        })),
    ];
    if (selected && !options.some(option => option.id.toLowerCase() === selectedKey)) {
      options.push({ id: selected, label: selected });
    }
    this.workerOptions = options;
  }

  private effectiveWorker(): string | undefined {
    return this.lockedWorker()?.trim() || this.workerFilter?.trim() || undefined;
  }

  private toAssignmentRow(item: MonitoredOperationListItemResponse): AssignmentRow {
    const outcome = assignmentOutcome(item);
    const timing = assignmentTiming(item);
    return {
      ...item,
      resultCount: readAssignmentResultCount(item),
      resultTooltip: formatAssignmentResultTooltip(item),
      durationLabel: timing.processing,
      queueLabel: timing.queue,
      outcomeLabel: outcome.label,
      outcomeReason: outcome.reason,
      hostLabel: assignmentHostLabel({
        kind: item.kind,
        startedAt: item.startedAt,
        ...readAssignmentHost(item),
      }),
      ...readAssignmentWorker(item),
      displayTitle: assignmentRowTitle(item.name, item.operationType, this.processes),
      stateSeverity: outcome.skipped ? 'secondary' : assignmentStateSeverity(item.state),
      statusAt: assignmentStatusAt(item.state, item.startedAt, item.completedAt),
      canCancel: canCancelAssignment(item.state),
      canRestart: canRestartAssignment(item.state),
      hasSatelliteProducts: assignmentHasSatelliteProducts(item.operationType),
      rss: readAssignmentRss(item),
    };
  }

  private keepListOnGetError(error: unknown) {
    if (error instanceof HttpErrorResponse && error.status === 0) {
      return EMPTY;
    }
    this.errorMessage = 'Failed to load assignments';
    return EMPTY;
  }

  private reloadOperations(): void {
    this.operationsReload$.next(false);
  }

  private createdBounds(): { createdFrom?: string; createdTo?: string } {
    // The worker card clips the list with activityFrom / activityTo, not creation time.
    if (this.lockedWindow()) {
      return {};
    }
    return createdRangeApiBounds(this.createdRange);
  }

  private activityBounds(): { activityFrom?: string; activityTo?: string } {
    const chartWindow = this.lockedWindow();
    if (!chartWindow) {
      return {};
    }
    return workerChartActivityBounds(chartWindow, new Date());
  }

  private resetToFirstPage(): void {
    this.page = 1;
    this.first = 0;
  }

  private tableFilters() {
    return {
      result: this.resultFilter,
      state: this.stateFilter,
      createdRange: this.createdRange,
      process: this.processFilter?.trim() ?? '',
      worker: this.workerSelectHidden() ? '' : (this.workerFilter?.trim() ?? ''),
      list: this.listMode,
      sort: this.sortColumn,
      dir: this.sortDir,
    };
  }

  private rebuildStateChips(): void {
    this.stateChips = ASSIGNMENT_STATE_FILTERS.map(id => ({
      id,
      label: id === 'all' ? 'All' : id,
      selected: this.stateFilter === id,
    }));
  }

  private rebuildListChips(): void {
    this.listChips = [
      { id: 'groups', label: 'By process', selected: this.listMode === 'groups' },
      { id: 'runs', label: 'Each run', selected: this.listMode === 'runs' },
    ];
  }

  private publishGroups(): void {
    const rows = this.rawGroups.map(row => toAssignmentGroupRow(row, this.processes));
    this.groups = sortAssignmentGroupRows(
      rows,
      { column: this.sortColumn, dir: this.sortDir },
      title => this.translate.instant(title),
      this.translate.currentLang,
    );
  }

  private feedRequestOptions(): { context: HttpContext } | undefined {
    const context = new HttpContext();
    let used = false;
    // An omitted Sort on the operations feed stays created desc. The runs default is state,
    // so that default is sent; created desc is the one pair this call can leave off.
    if (!(this.sortColumn === 'created' && this.sortDir === 'desc')) {
      context.set(ASSIGNMENT_FEED_SORT, {
        sort: this.sortColumn,
        dir: this.sortDir,
      });
      used = true;
    }
    const activity = this.activityBounds();
    if (activity.activityFrom && activity.activityTo) {
      context.set(ASSIGNMENT_ACTIVITY_WINDOW, {
        activityFrom: activity.activityFrom,
        activityTo: activity.activityTo,
      });
      used = true;
    }
    return used ? { context } : undefined;
  }

  private assignmentSortQueryNeedsRewrite(params: { get(name: string): string | null }): boolean {
    const serialized = serializeAssignmentTableFilterQuery(this.tableFilters());
    return (params.get('sort') ?? null) !== serialized['sort']
      || (params.get('dir') ?? null) !== serialized['dir'];
  }

  onSortColumn(column: AssignmentSortColumn): void {
    const next = toggleAssignmentColumnSort(
      this.listMode,
      { column: this.sortColumn, dir: this.sortDir },
      column,
    );
    if (next.column === this.sortColumn && next.dir === this.sortDir) {
      return;
    }
    this.sortColumn = next.column;
    this.sortDir = next.dir;
    this.sortAria = next.dir === 'asc' ? 'ascending' : 'descending';
    this.syncAssignmentFilterQuery();
  }

  private useDefaultSort(mode: AssignmentListMode): void {
    const defaults = defaultAssignmentSort(mode);
    this.sortColumn = defaults.column;
    this.sortDir = defaults.dir;
    this.sortAria = 'descending';
  }

  private syncAssignmentFilterQuery(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: serializeAssignmentTableFilterQuery(this.tableFilters()),
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private showNewAssignment(processKey: string): void {
    this.resultFilter = assignmentResultFilterAfterCreate();
    const key = canonicalProcessKey(processKey);
    this.processFilter = isOrchestrationProbeProcess(key) ? null : (key || null);
    this.rebuildStateChips();
    this.resetToFirstPage();
    this.syncAssignmentFilterQuery();
    this.reloadOperations();
  }

  create(): void {
    if (!this.allowCreate()) {
      return;
    }
    this.startForm()?.reset();
    this.dialogVisible = true;
  }

  onCatalogSearch(query: string): void {
    const next = query ?? '';
    if (next === this.searchQuery) {
      return;
    }
    this.searchQuery = next;
    this.resetToFirstPage();
    this.reloadOperations();
  }

  onResultFilterChange(filter: AssignmentResultFilter): void {
    if (this.resultFilter === filter) {
      return;
    }
    this.resultFilter = filter;
    this.syncAssignmentFilterQuery();
  }

  onProcessFilterChange(value: string | null): void {
    const next = value?.trim() || null;
    if ((this.processFilter ?? null) === next) {
      return;
    }
    this.processFilter = next;
    this.syncAssignmentFilterQuery();
  }

  onWorkerFilterChange(value: string | null): void {
    const next = value?.trim() || null;
    if ((this.workerFilter ?? null) === next) {
      return;
    }
    this.workerFilter = next;
    this.rebuildWorkerOptions();
    this.syncAssignmentFilterQuery();
  }

  selectStateFilter(filter: AssignmentStateFilter): void {
    if (this.stateFilter === filter) {
      return;
    }
    this.stateFilter = filter;
    this.rebuildStateChips();
    this.syncAssignmentFilterQuery();
  }

  selectListMode(mode: AssignmentListMode): void {
    if (this.listMode === mode) {
      return;
    }
    this.listMode = mode;
    this.rebuildListChips();
    this.useDefaultSort(mode);
    this.resetToFirstPage();
    this.syncAssignmentFilterQuery();
  }

  showProcessRuns(processKey: string): void {
    const next = openProcessGroupFilters(this.tableFilters(), processKey);
    if (!next) {
      return;
    }
    this.resultFilter = next.result;
    this.stateFilter = next.state;
    this.createdRange = next.createdRange;
    this.processFilter = next.process?.trim() || null;
    this.listMode = next.list ?? 'runs';
    this.sortColumn = next.sort ?? defaultAssignmentSort('runs').column;
    this.sortDir = next.dir ?? 'desc';
    this.sortAria = this.sortDir === 'asc' ? 'ascending' : 'descending';
    this.rebuildStateChips();
    this.rebuildListChips();
    this.resetToFirstPage();
    this.syncAssignmentFilterQuery();
  }

  openGroupAssignment(id: string): void {
    if (!id) {
      return;
    }
    void this.router.navigateByUrl(operationDetailsUrl('Assignment', id));
  }

  onCreatedRangeChange(value: Date[] | Date | null): void {
    if (Array.isArray(value)) {
      this.createdRange = value.filter((entry): entry is Date => entry instanceof Date);
    } else {
      this.createdRange = null;
    }
    this.syncAssignmentFilterQuery();
  }

  onPageChange(event: { first?: number; rows?: number }): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.pageSize;
    this.first = first;
    this.pageSize = rows;
    this.page = Math.floor(first / rows) + 1;
    this.reloadOperations();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.resetToFirstPage();
    this.reloadOperations();
  }

  start(): void {
    this.errorMessage = '';
    const built = this.startForm()?.build();
    if (!built) {
      return;
    }
    if (!built.ok) {
      this.errorMessage = built.error;
      return;
    }
    this.saving = true;
    const request: StartAssignmentProcessRequest = { parameters: built.parameters };
    this.assignments.assignmentsProcessesProcessKeyPost(this.processKey, request)
      .pipe(takeUntil(this.destroy$), finalize(() => { this.saving = false; this.cdr.markForCheck(); }))
      .subscribe({
        next: () => { this.dialogVisible = false; this.showNewAssignment(this.processKey); },
        error: error => this.errorMessage = assignmentStartApiError(error, 'Failed to create assignment'),
      });
  }

  showProducts(operation: MonitoredOperationListItemResponse): void {
    if (!operation.id) return;
    this.assignments.assignmentsAssignmentIdSatelliteProductsGet(operation.id, 0, 100).pipe(takeUntil(this.destroy$)).subscribe({
      next: products => {
        this.products = products.map(product => ({
          ...product,
          analysisUrl: orchestrationSatelliteProductUrl(product.id),
        }));
        this.setProductsVisible(true);
      },
      error: () => { this.errorMessage = 'Failed to load assignment products'; this.cdr.markForCheck(); },
    });
  }

  setProductsVisible(visible: boolean): void {
    this.productsVisible = visible;
    if (!visible) {
      this.products = [];
    }
    this.cdr.markForCheck();
  }

  makeSubscription(operation: MonitoredOperationListItemResponse): void {
    if (!operation.id) return;
    this.subscriptionDialog()?.open(
      operation.id,
      defaultSubscriptionNameFromAssignment({
        name: operation.name,
        operationType: operation.operationType,
      }),
    );
  }

  requestCancellation(operation: MonitoredOperationListItemResponse): void {
    if (!operation.id || !this.canCancel(operation)) return;
    this.cancellationAssignmentId = operation.id;
    this.cancellationVisible = true;
  }

  confirmCancellation(): void {
    if (!this.cancellationAssignmentId) return;
    this.cancellationSaving = true;
    this.errorMessage = '';
    this.assignments.assignmentsAssignmentIdCancelPost(this.cancellationAssignmentId)
      .pipe(takeUntil(this.destroy$), finalize(() => {
        this.cancellationSaving = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: () => {
          this.cancellationVisible = false;
          this.cancellationAssignmentId = '';
          this.load();
        },
        error: error => this.errorMessage = assignmentStartApiError(error, 'Failed to cancel assignment'),
      });
  }

  requestRestart(operation: MonitoredOperationListItemResponse): void {
    if (!operation.id || !this.canRestart(operation)) return;
    this.restartAssignmentId = operation.id;
    this.restartVisible = true;
  }

  confirmRestart(): void {
    if (!this.restartAssignmentId) return;
    this.restartSaving = true;
    this.errorMessage = '';
    this.assignments.assignmentsAssignmentIdRestartPost(this.restartAssignmentId)
      .pipe(takeUntil(this.destroy$), finalize(() => {
        this.restartSaving = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: () => {
          this.restartVisible = false;
          this.restartAssignmentId = '';
          this.load();
        },
        error: error => this.errorMessage = assignmentStartApiError(error, 'Failed to restart assignment'),
      });
  }

  requestRestartFailed(): void {
    if (!this.allowRestartFailed() || this.restartFailedSaving) {
      return;
    }
    this.restartFailedEveryProcess = !this.processFilter?.trim();
    this.restartFailedVisible = true;
  }

  closeRestartFailed(): void {
    if (this.restartFailedSaving) {
      return;
    }
    this.restartFailedVisible = false;
  }

  onRestartFailedVisible(visible: boolean): void {
    if (visible) {
      this.restartFailedVisible = true;
      return;
    }
    this.closeRestartFailed();
  }

  confirmRestartFailed(): void {
    if (!this.allowRestartFailed() || this.restartFailedSaving) {
      return;
    }
    this.restartFailedSaving = true;
    this.errorMessage = '';
    this.restartFailedApi.restart(this.processFilter).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.restartFailedSaving = false;
        this.cdr.markForCheck();
      }),
    ).subscribe({
      next: result => {
        this.restartFailedVisible = false;
        if (result.skipped > 0) {
          this.errorMessage = 'ORCH_RESTART_FAILED_AND_SKIPPED_PARTIAL';
        }
        this.load();
      },
      error: error => this.errorMessage = assignmentStartApiError(error, 'ORCH_RESTART_FAILED_AND_SKIPPED_ERROR'),
    });
  }

  requestCopy(operation: MonitoredOperationListItemResponse): void {
    if (!operation.id) return;
    this.copyAssignmentId = operation.id;
    this.copyProcessKey = operation.operationType ?? '';
    this.copyVisible = true;
  }

  confirmCopy(): void {
    if (!this.copyAssignmentId) return;
    this.copySaving = true;
    this.errorMessage = '';
    this.assignments.assignmentsAssignmentIdCopyPost(this.copyAssignmentId)
      .pipe(takeUntil(this.destroy$), finalize(() => {
        this.copySaving = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: () => {
          this.copyVisible = false;
          this.copyAssignmentId = '';
          this.showNewAssignment(this.copyProcessKey);
        },
        error: error => this.errorMessage = assignmentStartApiError(error, 'Failed to copy assignment'),
      });
  }

  openDetails(operation: MonitoredOperationListItemResponse): void {
    if (!operation.id) return;
    void this.router.navigateByUrl(operationDetailsUrl(operation.kind || 'Assignment', operation.id));
  }

  canCancel(operation: MonitoredOperationListItemResponse): boolean {
    return canCancelAssignment(operation.state);
  }

  canRestart(operation: MonitoredOperationListItemResponse): boolean {
    return canRestartAssignment(operation.state);
  }
}
